import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { useAdminUserDetail } from '@/features/admin/services/admin.convex'
import { AuthGuard } from '@/features/auth'
import { Badge } from '@/components/ui/badge'
import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

export const Route = createFileRoute('/(dashboard)/admin/users/$userId')({
  component: RouteComponent,
})

function RouteComponent() {
  const { userId } = Route.useParams()
  const navigate = useNavigate()
  const { location } = useRouterState()

  const { data: user, isLoading } = useAdminUserDetail(userId)

  function getTabFromRoute(pathname: string): string {
    if (pathname.includes('/settings')) return 'settings'
    return 'organizations'
  }

  const [tab, setTab] = useState(() => getTabFromRoute(location.pathname))

  useEffect(() => {
    setTab(getTabFromRoute(location.pathname))
  }, [location.pathname])

  function changeTab(newTab: string) {
    setTab(newTab)
    navigate({
      to: `/admin/users/${userId}/${newTab}`,
      search: { name: user?.name ?? undefined },
    })
  }

  if (isLoading) {
    return (
      <AuthGuard requiresPlatformAdmin>
        <div className="space-y-6">
          <div className="h-24 bg-muted animate-pulse rounded-lg" />
          <div className="h-64 bg-muted animate-pulse rounded-lg" />
        </div>
      </AuthGuard>
    )
  }

  if (!user) {
    return (
      <AuthGuard requiresPlatformAdmin>
        <div className="p-6 text-center text-muted-foreground">
          User not found.
        </div>
      </AuthGuard>
    )
  }

  return (
    <AuthGuard requiresPlatformAdmin>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 bg-muted/30 rounded-lg">
          <AvatarWithInitials
            name={user.name ?? user.email ?? user.id}
            src={user.image ?? undefined}
            className="h-12 w-12"
          />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold">
                {user.name ?? 'Unnamed'}
              </h1>
              {user.isPlatformAdmin && (
                <Badge variant="default">Platform Admin</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            {user.username && (
              <p className="text-xs text-muted-foreground">@{user.username}</p>
            )}
            <p className="text-xs text-muted-foreground font-mono mt-0.5">
              {new Date(user.createdAt).toLocaleDateString()} · ID: {user.id}
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={changeTab}>
          <TabsList>
            <TabsTrigger value="organizations">
              Organizations ({user.organizations.length})
            </TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          <div className="mt-4">
            <Outlet />
          </div>
        </Tabs>
      </div>
    </AuthGuard>
  )
}
